import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, LinearProgress, Paper,
  Step, StepButton, Stepper, Typography,
} from '@mui/material';
import {
  Business, Checklist, DateRange, Description, Groups, LocationOn,
  MonetizationOn, RoomService,
} from '@mui/icons-material';
import { apiFetch } from '../../utils/apiFetch.js';
import MovdLogo from '../../components/MovdLogo.jsx';
import {
  answersFromProfile, defaultAnswers, mapMoverUpdates, mapPricingPayload,
  sectionCompletion,
} from './onboardingMapping.js';
import CompanyStep from './steps/CompanyStep.jsx';
import ServicesStep from './steps/ServicesStep.jsx';
import ServiceAreaStep from './steps/ServiceAreaStep.jsx';
import CrewsEquipmentStep from './steps/CrewsEquipmentStep.jsx';
import PricingStep from './steps/PricingStep.jsx';
import AvailabilityStep from './steps/AvailabilityStep.jsx';
import DocumentsStep from './steps/DocumentsStep.jsx';
import ReviewStep from './steps/ReviewStep.jsx';

const STEPS = [
  { id: 'company', label: 'Company', icon: <Business fontSize="small" /> },
  { id: 'services', label: 'Services', icon: <RoomService fontSize="small" /> },
  { id: 'serviceArea', label: 'Service Area', icon: <LocationOn fontSize="small" /> },
  { id: 'crews', label: 'Crews & Equipment', icon: <Groups fontSize="small" /> },
  { id: 'pricing', label: 'Pricing', icon: <MonetizationOn fontSize="small" /> },
  { id: 'availability', label: 'Availability', icon: <DateRange fontSize="small" /> },
  { id: 'documents', label: 'Documents', icon: <Description fontSize="small" /> },
  { id: 'review', label: 'Review & Submit', icon: <Checklist fontSize="small" /> },
];

export default function MoverOnboardingWizard({ profile, token, onComplete, onProfileUpdate, onLogout }) {
  const [answers, setAnswers] = useState(() => answersFromProfile(profile || {}));
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pricingStatus, setPricingStatus] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  const authHeaders = useMemo(
    () => (token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // Load existing pricing config so the wizard pre-fills and can show readiness.
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/moverPricing/me', { headers: authHeaders })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        const { pricingStatus: status, ...pricing } = data;
        setPricingStatus(status || null);
        setAnswers(current => {
          // Only hydrate pricing fields if the mover hasn't started editing.
          const hydrated = answersFromProfile(profile || {}, pricing, null);
          return { ...current, pricing: hydrated.pricing, crews: { ...current.crews, crewSizes: hydrated.crews.crewSizes } };
        });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoadingPricing(false));
    return () => { cancelled = true; };
  }, [authHeaders]);

  const validateStep = step => {
    const newErrors = {};
    if (step === 0) {
      if (!answers.company.companyName.trim()) newErrors.companyName = 'Company name is required';
      if (!answers.company.phone.trim()) newErrors.phone = 'Phone number is required';
    }
    if (step === 1 && !Object.values(answers.services).some(Boolean)) {
      newErrors.services = 'Select at least one service';
    }
    if (step === 2) {
      if (!answers.serviceArea.homeProvince) newErrors.homeProvince = 'Choose your home province';
      if (!answers.serviceArea.homeCity) newErrors.homeCity = 'Choose your home city';
      if (answers.services.longDistance && answers.serviceArea.destinationProvinces.length === 0) {
        newErrors.destinationProvinces = 'Pick at least one province you move customers to';
      }
    }
    if (step === 3 && answers.crews.crewSizes.length === 0) {
      newErrors.crewSizes = 'Select at least one crew size';
    }
    if (step === 4) {
      const p = answers.pricing;
      const hasRate = Object.values(p.crewRates).some(v => Number(v) > 0) || Number(p.longDistance.flatRate) > 0;
      if (!hasRate) newErrors.rates = 'Enter at least one hourly crew rate or a flat rate';
      if (!Number(p.minimumHours) > 0) newErrors.minimumHours = 'Required';
      if (answers.services.longDistance && p.longDistance.mode !== 'custom') {
        const ok = (p.longDistance.mode === 'per_km' && Number(p.longDistance.ratePerKm) > 0)
          || (p.longDistance.mode === 'per_mile' && Number(p.longDistance.ratePerMile) > 0)
          || (p.longDistance.mode === 'flat' && Number(p.longDistance.flatRate) > 0);
        if (!ok) newErrors.longDistance = 'Enter your long-distance rate, or choose Custom';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveSection = async step => {
    if (step === 4 || step === 3 || step === 2 || step === 1 || step === 0) {
      // Company/coverage answers live on MoverCompany.
      const response = await apiFetch('/api/movers/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(mapMoverUpdates(answers)),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to save your profile');
      }
    }
    if (step === 4) {
      const response = await apiFetch('/api/moverPricing/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(mapPricingPayload(answers)),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to save pricing');
      setPricingStatus(data.pricingStatus || null);
    }
    if (step === 5) {
      const availability = answers.availability;
      const body = {
        timezone: availability.timezone,
        bufferMinutes: availability.bufferMinutes,
        ...(availability.weekly ? { weekly: availability.weekly } : {}),
        exceptions: availability.exceptions || [],
      };
      const response = await apiFetch('/api/calendar/availability', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Unable to save availability');
      }
    }
    if (step === 6 && answers.documents.length) {
      const formData = new FormData();
      answers.documents.forEach(file => formData.append('documents', file));
      const response = await apiFetch('/api/movers/upload-documents', {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Document upload failed');
      }
    }
  };

  const handleContinue = async () => {
    if (!validateStep(activeStep)) return;
    setSaving(true);
    setSaveError('');
    try {
      await saveSection(activeStep);
      if (activeStep < STEPS.length - 1) setActiveStep(s => s + 1);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError('');
    try {
      // Save everything in one pass so nothing is half-configured.
      for (const step of [0, 4, 5, 6]) await saveSection(step);
      await onProfileUpdate?.({ onboardingCompleted: true, onboardingSkipped: false });
      onComplete?.();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const completion = sectionCompletion(answers);
  const completionValues = [completion.company, completion.services, completion.serviceArea, completion.crews, completion.pricing, completion.availability, completion.documents];
  const completedCount = completionValues.filter(Boolean).length;
  const allRequiredDone = completedCount === completionValues.length;

  const stepProps = { answers, errors, onChange: setAnswers, token };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', py: { xs: 2, md: 5 }, px: { xs: 1.5, md: 2 } }}>
      <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1.5px solid #E2E8F0' }}>
        <Box sx={{
          p: { xs: 2.5, md: 3.5 }, color: '#fff',
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #1E293B 100%)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <MovdLogo size="medium" variant="full" color="white" />
            <Typography variant="overline" sx={{ color: '#93C5FD', fontWeight: 700, letterSpacing: '0.12em' }}>
              Partner setup
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.01em' }}>
            Set up your MOVD partner profile
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.82)', mb: 2, maxWidth: 560 }}>
            {completedCount} of {completionValues.length} sections complete — approval is required before customers can book you.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(completedCount / completionValues.length) * 100}
            sx={{ bgcolor: 'rgba(255,255,255,0.22)', '& .MuiLinearProgress-bar': { bgcolor: '#38BDF8' } }}
          />
        </Box>

        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, bgcolor: 'background.paper', borderBottom: '1px solid #E0E6ED' }}>
          <Stepper nonLinear activeStep={activeStep} alternativeLabel>
            {STEPS.map((step, index) => (
              <Step key={step.id} completed={index < completionValues.length ? completionValues[index] : false}
                sx={{
                  '& .MuiStepLabel-label': { fontSize: '0.78rem', fontWeight: 600 },
                  '& .Mui-active.MuiStepIcon-root': { color: '#2563EB' },
                  '& .Mui-completed.MuiStepIcon-root': { color: '#17B046' },
                }}>
                <StepButton icon={step.icon} onClick={() => setActiveStep(index)}>
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default' }}>
          {loadingPricing && activeStep === 4 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <>
              {activeStep === 0 && <CompanyStep {...stepProps} />}
              {activeStep === 1 && <ServicesStep {...stepProps} />}
              {activeStep === 2 && <ServiceAreaStep {...stepProps} />}
              {activeStep === 3 && <CrewsEquipmentStep {...stepProps} />}
              {activeStep === 4 && <PricingStep {...stepProps} pricingStatus={pricingStatus} />}
              {activeStep === 5 && <AvailabilityStep {...stepProps} />}
              {activeStep === 6 && <DocumentsStep {...stepProps} />}
              {activeStep === 7 && (
                <ReviewStep answers={answers} pricingStatus={pricingStatus} onGoToStep={setActiveStep} />
              )}
            </>
          )}

          {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              {onLogout && <Button color="inherit" onClick={onLogout}>Log out</Button>}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="outlined" disabled={activeStep === 0 || saving} onClick={() => setActiveStep(s => s - 1)}>
                Back
              </Button>
              {activeStep < STEPS.length - 1 ? (
                <Button variant="contained" onClick={handleContinue} disabled={saving}>
                  {saving ? 'Saving…' : 'Save & continue'}
                </Button>
              ) : (
                <Button
                  variant="contained" color="success" onClick={handleSubmit}
                  disabled={saving || !allRequiredDone}
                >
                  {saving ? 'Submitting…' : 'Submit for review'}
                </Button>
              )}
            </Box>
          </Box>
          {activeStep === STEPS.length - 1 && !allRequiredDone && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'right' }}>
              Complete every section above to submit for review.
            </Typography>
          )}
        </Box>
      </Paper>
      </Box>
    </Box>
  );
}
